// src/hooks/useUnsavedChanges.js
//
// "Has the person changed anything on this page that isn't saved?" for an edit page.
//
//   const { dirty, onInteract } = useUnsavedChanges(snapshot, ready);
//   <div onPointerDownCapture={onInteract} onKeyDownCapture={onInteract}>…</div>
//
// snapshot  any value describing everything a save would write (compared as JSON)
// ready     true once the page has finished loading
//
// The baseline FOLLOWS the snapshot while the page loads and settles (records arriving,
// values the page fills in itself), and is FIXED the first time the person presses a
// key or clicks on the page after it's ready. The press is seen before the change it
// makes, so that change is never absorbed into the baseline. From then on `dirty` is
// true while the snapshot differs from the baseline: changing something and changing
// it back is not dirty.
//
// While dirty, closing or reloading the tab asks first (the browser's own prompt).

import { useCallback, useEffect, useRef, useState } from "react";

export default function useUnsavedChanges(snapshot, ready) {
  const current = JSON.stringify(snapshot);
  const baselineRef = useRef(current);
  const frozenRef = useRef(false);
  const [, rerender] = useState(0);

  // Follow the page until the person first interacts with it.
  if (!frozenRef.current) baselineRef.current = current;

  const onInteract = useCallback(() => {
    if (ready && !frozenRef.current) {
      frozenRef.current = true;
      rerender((n) => n + 1);
    }
  }, [ready]);

  const dirty = frozenRef.current && current !== baselineRef.current;

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // absorb({ key: value }): these keys were just saved by something else (e.g. a
  // health-survey answer arriving while the page is open), so the baseline takes those
  // values. The person's own edits elsewhere stay dirty. Optional; callers that don't
  // use it are unchanged.
  const absorb = useCallback((patch) => {
    try {
      baselineRef.current = JSON.stringify({ ...JSON.parse(baselineRef.current), ...patch });
    } catch {
      // A baseline that isn't an object: leave it.
    }
    rerender((n) => n + 1);
  }, []);

  return { dirty, onInteract, absorb };
}
